import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { PlaceValueOptions, ProblemData, isScientificQuestion } from "./Constants.ts";
import { generateScientificNotationPages } from "./ScientificNotationGenerator.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { placeClauses as enClauses, placeValuePrompts as en } from "../Files/Prompts/en/placeValue.ts";
import { placeClauses as esClauses, placeValuePrompts as es } from "../Files/Prompts/es/placeValue.ts";
import { placeClauses as frClauses, placeValuePrompts as fr } from "../Files/Prompts/fr/placeValue.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const PLACE_CLAUSES: Record<PrintedLanguage, typeof enClauses> = { en: enClauses, es: esClauses, fr: frClauses };

/**
 * Place value problem generation.
 *
 * Unlike every other format here, these are not arithmetic: there are no
 * operands and no operator, just a number read one way and written another.
 * That is why place value ignores the operator selection entirely - a question
 * about what the 7 in 5,724 is worth has no addition or subtraction in it.
 *
 * Each problem carries its prompt and its answer as text, in
 * ProblemData.display, because both are sentences rather than numbers.
 */

/** 54321 -> "54,321". Written out rather than using toLocaleString so the
 *  output cannot vary with the environment's locale. */
export function withThousands(value: number): string {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** The smallest and largest number with exactly `digits` digits. */
export function rangeForDigits(digits: number): [number, number] {
    const safe = Math.max(2, Math.min(5, digits));
    return [Math.pow(10, safe - 1), Math.pow(10, safe) - 1];
}

/**
 * The non-zero parts of a number, largest first: 4302 -> [4000, 300, 2].
 *
 * Zero columns are left out because that is how expanded form is written -
 * 4302 is 4000 + 300 + 2, not 4000 + 300 + 0 + 2.
 */
export function expandedParts(value: number): number[] {
    const parts: number[] = [];
    const digits = String(value).split("");
    digits.forEach((digit, index) => {
        const place = Math.pow(10, digits.length - index - 1);
        const part = Number(digit) * place;
        if (part !== 0) {
            parts.push(part);
        }
    });
    return parts;
}

/** "4,000 + 300 + 2" */
export function expandedForm(value: number): string {
    return expandedParts(value).map(withThousands).join(" + ");
}

/**
 * A number with no zero digit, for the digit-value question.
 *
 * Asking what the 0 in 4,032 is worth has a correct answer of zero, which
 * reads as a trick rather than a check of understanding.
 */
function numberWithoutZeros(digits: number): number {
    let built = "";
    for (let i = 0; i < digits; i++) {
        built += String(getRandomInt(1, 9));
    }
    return Number(built);
}

/**
 * A number with at least two non-zero digits, for the two forms questions.
 *
 * 80 is 80 in expanded form, and so is 600 and 4,000: the question and its
 * answer are the same text, which asks a student nothing. About one two-digit
 * number in ten was one of those, so one tens-and-ones sheet in two printed
 * "80 = 80". Redrawn rather than nudged, so every value stays equally likely.
 */
function numberWithSeveralParts(low: number, high: number): number {
    let value = getRandomInt(low, high);
    for (let attempt = 0; attempt < 20 && expandedParts(value).length < 2; attempt++) {
        value = getRandomInt(low, high);
    }
    return value;
}

/** Which question types a setting asks for. Mixed means all three. */
function questionTypes(question: PlaceValueOptions): PlaceValueOptions[] {
    if (question === PlaceValueOptions.Mixed) {
        return [
            PlaceValueOptions.ExpandedForm,
            PlaceValueOptions.StandardForm,
            PlaceValueOptions.DigitValue,
        ];
    }
    return [question];
}

/** One place value question, as a prompt and the answer it expects. */
export function buildPlaceValueProblem(
    question: PlaceValueOptions,
    digits: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    const [low, high] = rangeForDigits(digits);

    switch (question) {
        case PlaceValueOptions.StandardForm: {
            const value = numberWithSeveralParts(low, high);
            return {
                key,
                numbers: [value],
                operator: "",
                display: [`${expandedForm(value)} =`, withThousands(value)],
            };
        }
        case PlaceValueOptions.DigitValue: {
            const value = numberWithoutZeros(digits);
            const text = String(value);
            const position = getRandomInt(0, text.length - 1);
            const digit = Number(text[position]);
            const place = Math.pow(10, text.length - position - 1);
            const placeClause = PLACE_CLAUSES[language][text.length - position - 1];
            return {
                key,
                numbers: [value, digit, digit * place],
                operator: "",
                display: [
                    PROMPTS[language].digitValue(withThousands(value), digit, placeClause),
                    withThousands(digit * place),
                ],
            };
        }
        default: {
            const value = numberWithSeveralParts(low, high);
            return {
                key,
                numbers: [value],
                operator: "",
                display: [`${withThousands(value)} =`, expandedForm(value)],
            };
        }
    }
}

/** A page-shaped set of place value questions. */
export function generatePlaceValuePages(settings: ISettingsProps): ProblemData[][] | null {
    // The two scientific-notation types (ADR 0130) deal their own pages. This
    // is ahead of every draw, so the older types' sequences are untouched.
    if (isScientificQuestion(settings.PlaceValueQuestion)) {
        return generateScientificNotationPages(settings);
    }
    const types = questionTypes(settings.PlaceValueQuestion ?? PlaceValueOptions.Mixed);
    const digits = settings.PlaceValueDigits ?? 4;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (type, key) => buildPlaceValueProblem(type, digits, key, language),
        fingerprint: (problem) => problem.display![0],
    });
}
